"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, Megaphone, Plus } from "lucide-react";

import { CrmSettings } from "./crm-settings";
import { AgentDeliverySettings } from "@/components/deliveries/agent-delivery-settings";
import { MetaAdsSetupDialog } from "@/components/integrations/meta-ads/meta-ads-setup-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useMetaAdsIntegrationQuery } from "@/hooks/queries/meta-ads";
import { usePermissions } from "@/hooks/use-permissions";
import { INTEGRATIONS_PERMISSIONS } from "@/lib/auth/permissions.constants";

export function IntegrationsSettings({
  agentId,
  agentName,
}: {
  agentId: string;
  agentName: string;
}) {
  const router = useRouter();
  const { can } = usePermissions();
  const canViewIntegrations = can(INTEGRATIONS_PERMISSIONS.VIEW);
  const canManageIntegrations = can(INTEGRATIONS_PERMISSIONS.MANAGE);
  const integrationQuery = useMetaAdsIntegrationQuery(canViewIntegrations);
  const [setupOpen, setSetupOpen] = useState(false);
  const integration = integrationQuery.data ?? null;

  function openAdsPlatforms() {
    router.push("?page=integrations&section=ads-platforms");
  }

  return (
    <div className="space-y-6">
      <CrmSettings agentId={agentId} compact />

      <AgentDeliverySettings agentId={agentId} scopeType="voice_agent" compact />

      <Card className="flex min-h-80 flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            <span>Ads Platforms</span>
          </CardTitle>
          <CardDescription>
            Send new ad leads to this Voice Agent for a timely follow-up.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          {!canViewIntegrations ? (
            <Alert>
              <AlertDescription>
                You do not have permission to view ads platform settings.
              </AlertDescription>
            </Alert>
          ) : null}

          {canViewIntegrations && integrationQuery.isLoading ? (
            <div className="h-40 animate-pulse rounded-lg bg-muted" />
          ) : null}

          {canViewIntegrations && integrationQuery.error ? (
            <Alert variant="destructive">
              <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                <span>Meta Ads settings could not be loaded.</span>
                <Button type="button" size="sm" variant="outline" onClick={() => void integrationQuery.refetch()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {canViewIntegrations && !integrationQuery.isLoading && !integrationQuery.error && !integration ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <h2 className="text-xl font-semibold tracking-tight">Connect Meta Ads</h2>
              <p className="mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
                <span>{`Connect Meta Ads to create a follow-up task for ${agentName} whenever someone submits an Instant Form.`}</span>
              </p>
              <p className="mt-2 max-w-lg text-sm text-muted-foreground">
                You can choose the Facebook Page, ad account, and Instant Forms during setup.
              </p>
              {canManageIntegrations ? (
                <Button className="mt-4" type="button" onClick={() => setSetupOpen(true)}>
                  <Plus aria-hidden="true" className="mr-2 h-4 w-4" />
                  <span>Connect Meta Ads</span>
                </Button>
              ) : null}
            </div>
          ) : null}

          {canViewIntegrations && !integrationQuery.isLoading && !integrationQuery.error && integration ? (
            <MetaAdsAgentConnection
              agentId={agentId}
              agentName={agentName}
              connectedAgentId={integration.voice_agent_id}
              connectedAgentName={integration.voice_agent_name}
              isConnected={integration.status === "connected"}
              onManage={openAdsPlatforms}
            />
          ) : null}
        </CardContent>
      </Card>

      <MetaAdsSetupDialog
        open={setupOpen}
        integration={null}
        initialVoiceAgentId={agentId}
        onOpenChange={setSetupOpen}
      />
    </div>
  );
}

function MetaAdsAgentConnection({
  agentId,
  agentName,
  connectedAgentId,
  connectedAgentName,
  isConnected,
  onManage,
}: {
  agentId: string;
  agentName: string;
  connectedAgentId: string | null;
  connectedAgentName: string | null;
  isConnected: boolean;
  onManage: () => void;
}) {
  if (!isConnected) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-2xl text-sm text-muted-foreground">
          <span>{`Meta Ads setup needs attention. Finish it from the Integrations page before assigning leads to ${agentName}.`}</span>
        </p>
        <Button type="button" variant="outline" onClick={onManage}>
          <span>Open Meta Ads settings</span>
          <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  }

  if (connectedAgentId === agentId) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">Meta Ads is connected to this agent</span>
            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Meta Ads</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            <span>{`New Instant Form leads are sent to ${agentName}.`}</span>
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onManage}>
          <span>Manage Meta Ads</span>
          <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <p className="max-w-2xl text-sm text-muted-foreground">
        <span>{`Meta Ads is connected to ${connectedAgentName ?? "another Voice Agent"}. Change the callback agent from the Integrations page.`}</span>
      </p>
      <Button type="button" variant="outline" onClick={onManage}>
        <span>Open Meta Ads settings</span>
        <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}
